import { memo, useState } from "react";

import newTheme from "@/new_theme";
import { Checkbox } from "@components/Field";
import ReactWordcloud, { OptionsProp } from "@cyberblast/react-wordcloud";

import { BaseWidget } from "../BaseWidget";
import { PackedWidgetData, unpackWidgetData } from "./utils";
import { WidgetProps } from "./WidgetProps";

const WORD_CLOUD_COLORS = [
  newTheme["primary-800"],
  newTheme["pink-500"],
  newTheme["blue-600"],
];

const WORD_CLOUD_SETTINGS: OptionsProp = {
  deterministic: true,
  colors: WORD_CLOUD_COLORS,
  enableTooltip: true,
  fontFamily: newTheme.fonts.header,
  fontWeight: "500",
  fontSizes: [30, 150],
  rotations: 3,
  rotationAngles: [0, 0],
};

type Dataset = {
  label: string[];
  data: number[];
};

type InsightsWordCloudData = PackedWidgetData<Dataset>;

const InsightsWordCloud: React.FunctionComponent<
  WidgetProps<InsightsWordCloudData>
> = (props) => {
  const { data } = props;
  const chartData = unpackWidgetData<Dataset>(data);
  const [showHashTags, setShowHashTags] = useState(true);

  const hasData = data?.some(({ results }) =>
    results?.dataset?.some(({ data }) => data?.some((d: number) => d > 0))
  );

  const words = chartData.datasets[0]?.data.map((value, index) => ({
    text: chartData.datasets[0].label[index],
    value: value,
  }));

  const hasHasTags = words?.some((word) => word.text.startsWith("#"));
  const filteredWords = words?.filter(
    (word) => showHashTags || !word.text.startsWith("#")
  );

  return (
    <BaseWidget
      {...props}
      tools={
        hasHasTags ? (
          <Checkbox
            defaultChecked={showHashTags}
            onChange={(e) => setShowHashTags(e.target.checked)}
            label={
              L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_WORD_CLOUD_TOGGLE_HASHTAGS
            }
          />
        ) : null
      }
      hasData={hasData}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_WORD_CLOUD}
      description={L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_WORD_CLOUD_SUBTITLE}
    >
      {!!chartData && (
        <ReactWordcloud words={filteredWords} options={WORD_CLOUD_SETTINGS} />
      )}
    </BaseWidget>
  );
};

export const WordCloud = memo(InsightsWordCloud);
